import { coverReady, type Cover, type Report, type ReportIssue } from './model';

export type ReportAction = 'dismiss' | 'suppress' | 'replace' | 'resolve';
export interface ReportDecisionDraft {
  action: ReportAction;
  moderatorMessage: string;
  resolveRelated: boolean;
  cover?: Cover;
  loadedCoverUrl?: string | null;
  description?: string;
}

export function relatedReportGroupComplete(issue: ReportIssue): boolean {
  const shown = issue.relatedReports?.length ?? 0;
  const total = issue.relatedReportCount ?? shown;
  return !issue.relatedReportsTruncated && total === shown && total <= 25;
}

export function inspectableCoverUrl(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password ? value : null;
  } catch {
    return null;
  }
}

/** One issue per decision: the untouched target remains independently pending. */
export function reportDecisionBody(report: Report, issue: ReportIssue, draft: ReportDecisionDraft) {
  const message = draft.moderatorMessage.trim();
  if (report.status !== 'pending' || issue.status !== 'pending' || !issue.id || !message)
    return null;
  const content = issue.target !== 'book';
  if (content && draft.resolveRelated && !relatedReportGroupComplete(issue)) return null;
  if (
    content &&
    (!issue.versionKey ||
      !Number.isSafeInteger(issue.contentVersion) ||
      (issue.contentVersion ?? 0) < 1)
  )
    return null;
  if (content ? draft.action === 'resolve' : ['suppress', 'replace'].includes(draft.action))
    return null;
  let replacement: { url?: string; description?: string; rightsNote?: string } | undefined;
  if (draft.action === 'replace') {
    if (issue.target === 'cover') {
      const cover = draft.cover;
      if (
        !cover ||
        cover.policy !== 'moderator' ||
        !coverReady(cover, draft.loadedCoverUrl ?? null) ||
        cover.url === issue.content?.url
      )
        return null;
      replacement = {
        url: cover.url,
        ...(cover.rightsNote?.trim() ? { rightsNote: cover.rightsNote.trim() } : {}),
      };
    } else {
      const description = draft.description?.trim();
      if (
        !description ||
        description === issue.content?.description?.trim() ||
        description.length > 12000
      )
        return null;
      replacement = { description };
    }
  }
  return {
    expectedVersion: report.version,
    moderatorMessage: message,
    issues: [
      {
        issueId: issue.id,
        action: draft.action,
        expectedContentVersion: issue.contentVersion,
        resolveRelated: content && draft.resolveRelated,
        ...(replacement ? { replacement } : {}),
      },
    ],
  };
}
